import React from 'react';
import Box from '@mui/material/Box';
import { ChannelSettingsProvider, useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import EditDetailsModal from '@sendbird/uikit-react/ChannelSettings/components/EditDetailsModal';

interface EditChannelModalProps {
  channelUrl: string;
  onClose: () => void;
}

/**
 * Inner component that defers rendering EditDetailsModal until the channel has
 * been fetched by ChannelSettingsProvider. Sendbird's Input component uses
 * `useState(value)` which only captures the initial value — if the modal
 * renders while the channel is still loading, the name field stays empty.
 */
const ContextReadyBoundary: React.FC<{ onSubmit: () => void; onCancel: () => void }> = ({
  onSubmit,
  onCancel,
}) => {
  const { channel, loading } = useChannelSettingsContext();
  if (loading || !channel) return null;
  return <EditDetailsModal onSubmit={onSubmit} onCancel={onCancel} />;
};

/**
 * Thin wrapper that renders Sendbird's EditDetailsModal (channel name + picture edit)
 * outside of the ChannelSettings panel. Wraps in ChannelSettingsProvider to supply
 * the required context. z-index 1700 ensures it renders above the chat drawer (1600).
 */
const EditChannelModal: React.FC<EditChannelModalProps> = ({ channelUrl, onClose }) => (
  <Box sx={{ position: 'fixed', zIndex: 'chatPopup' }}>
    <ChannelSettingsProvider channelUrl={channelUrl} onCloseClick={onClose}>
      <ContextReadyBoundary onSubmit={onClose} onCancel={onClose} />
    </ChannelSettingsProvider>
  </Box>
);

export default EditChannelModal;
